import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Collapse,
  CircularProgress,
  Alert,
  Box,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Button
} from '@mui/material';
import { ArrowBack, PlayArrow, KeyboardArrowDown, KeyboardArrowUp, ContentCopy, Timeline } from '@mui/icons-material';
import axios from 'axios';
import config from '../config';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

function formatCallTime(date) {
  if (!date) return 'NA';
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

function formatDuration(seconds) {
  if (!seconds) return 'N/A';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

function getStatusColor(status) {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'processing':
      return 'warning';
    case 'new':
      return 'default';
    default:
      return 'default';
  }
}

const CallReportList = () => {
  const { customAgentId } = useParams();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 14)), // 2 weeks back
    endDate: new Date() // current date
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [agentSettings, setAgentSettings] = useState({
    hideTranscriptAudio: false,
    hideBeforeDate: null
  });
  const [user, setUser] = useState(null);
  const itemsPerPage = 20;

  useEffect(() => {
    // Get user from localStorage and set it
    const currentUser = JSON.parse(localStorage.getItem('user'));
    if (currentUser) {
      setUser(currentUser);
    }

    const fetchAgentSettings = async () => {
      try {
        // Get the user from localStorage
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) {
          return;
        }

        // Determine the agent ID to use
        const agentId = user.role === 'admin' ? customAgentId : user.customAgentId;
        if (!agentId) {
          return;
        }

        // Fetch agent settings
        const agentRes = await axios.get(`${config.apiBaseUrl}/api/agents/custom/${agentId}`);
        if (agentRes.data) {
          setAgentSettings({
            hideTranscriptAudio: agentRes.data.hideTranscriptAudio || false,
            hideBeforeDate: agentRes.data.hideBeforeDate ? new Date(agentRes.data.hideBeforeDate) : null
          });
        }
      } catch (err) {
        console.error('Error fetching agent settings:', err);
        // Don't set error here, just use defaults
      }
    };

    fetchAgentSettings();
  }, [customAgentId]);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Get the user from localStorage
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user) {
          throw new Error('User not authenticated');
        }

        // Determine the agent ID to use
        const agentId = user.role === 'admin' ? customAgentId : user.customAgentId;
        if (!agentId) {
          throw new Error('Agent ID not found');
        }

        // Get the reports with pagination
        const reportsRes = await axios.get(`${config.apiBaseUrl}/api/call-reports/agent/${agentId}`, {
          params: {
            page,
            limit: itemsPerPage
          }
        });
        
        if (reportsRes.data && reportsRes.data.reports) {
          // Ensure each report has an action_done field
          const reportsWithStatus = reportsRes.data.reports.map(report => ({
            ...report,
            action_done: report.action_done || 'pending'
          }));
          
          // Sort by callTime descending
          const sorted = reportsWithStatus.sort(
            (a, b) => new Date(b.callTime) - new Date(a.callTime)
          );
          
          setReports(sorted);
          
          // Update pagination info
          if (reportsRes.data.pagination) {
            setTotalPages(reportsRes.data.pagination.totalPages);
            setTotalItems(reportsRes.data.pagination.total);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.message || 'Failed to fetch reports');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [customAgentId, page]);

  const handleRowExpand = (reportId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  const handleCopyTranscript = (transcript) => {
    // Replace <strong> tags with bold text
    const formattedTranscript = transcript
      .replace(/<strong>/g, '')
      .replace(/<\/strong>/g, '');
    navigator.clipboard.writeText(formattedTranscript);
  };

  const handleStatusChange = async (reportId, checked) => {
    try {
      setUpdatingStatus(prev => ({ ...prev, [reportId]: true }));
      
      // Update the UI immediately
      setReports(prevReports => 
        prevReports.map(report => 
          report._id === reportId 
            ? { ...report, action_done: checked ? 'completed' : 'pending' }
            : report
        )
      );

      // Update the backend
      const response = await axios.patch(
        `${config.apiBaseUrl}/api/call-reports/${reportId}`,
        { action_done: checked ? 'completed' : 'pending' },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.data || !response.data.action_done) {
        throw new Error('Invalid response from server');
      }

      // Update the UI with the response data
      setReports(prevReports => 
        prevReports.map(report => 
          report._id === reportId 
            ? { ...report, action_done: response.data.action_done }
            : report
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
      // Revert the UI change if the update fails
      setReports(prevReports => 
        prevReports.map(report => 
          report._id === reportId 
            ? { ...report, action_done: !checked ? 'completed' : 'pending' }
            : report
        )
      );
      setError(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [reportId]: false }));
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Calculate total seconds: callDuration + (totalCallDuration - callDuration)
  // Only if totalCallDuration is numeric and > callDuration
  const calculateTotalSeconds = () => {
    return filteredReports.reduce((total, report) => {
      const callDuration = report.callDuration || 0;
      const totalCallDuration = report.totalCallDuration;
      
      // Check if totalCallDuration is numeric and greater than callDuration
      if (totalCallDuration !== null && totalCallDuration !== undefined && 
          typeof totalCallDuration === 'number' && 
          !isNaN(totalCallDuration) && 
          totalCallDuration > callDuration) {
        // Total = callDuration + (totalCallDuration - callDuration) = totalCallDuration
        return total + totalCallDuration;
      } else {
        // Fallback to callDuration if totalCallDuration is not valid
        return total + callDuration;
      }
    }, 0);
  };

  // Calculate call duration for a single report (for agent view)
  // Returns: callDuration + (totalCallDuration - callDuration) if totalCallDuration is valid
  const calculateCallDuration = (report) => {
    const callDuration = report.callDuration || 0;
    const totalCallDuration = report.totalCallDuration;
    
    // Check if totalCallDuration is numeric and greater than callDuration
    if (totalCallDuration !== null && totalCallDuration !== undefined && 
        typeof totalCallDuration === 'number' && 
        !isNaN(totalCallDuration) && 
        totalCallDuration > callDuration) {
      // Return totalCallDuration (which is callDuration + transferDuration)
      return totalCallDuration;
    } else {
      // Fallback to callDuration
      return callDuration;
    }
  };

  // Calculate total agent duration (callDuration)
  const calculateTotalAgentDuration = () => {
    return filteredReports.reduce((total, report) => {
      return total + (report.callDuration || 0);
    }, 0);
  };

  // Calculate total transfer duration (totalCallDuration - callDuration)
  // Only if totalCallDuration is numeric and > callDuration
  const calculateTotalTransferDuration = () => {
    return filteredReports.reduce((total, report) => {
      const callDuration = report.callDuration || 0;
      const totalCallDuration = report.totalCallDuration;
      
      // Check if totalCallDuration is numeric and greater than callDuration
      if (totalCallDuration !== null && totalCallDuration !== undefined && 
          typeof totalCallDuration === 'number' && 
          !isNaN(totalCallDuration) && 
          totalCallDuration > callDuration) {
        return total + (totalCallDuration - callDuration);
      }
      return total; // Don't add anything if conditions not met
    }, 0);
  };

  // Calculate transfer duration for a single report
  const calculateTransferDuration = (report) => {
    const totalDuration = report.totalCallDuration || 0;
    const callDuration = report.callDuration || 0;
    const transferDuration = totalDuration - callDuration;
    return Math.max(0, transferDuration); // Ensure non-negative
  };

  // Format total seconds to hours, minutes, seconds
  const formatTotalDuration = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  // Helper function to check if transcript and audio should be hidden for a report
  const shouldHideTranscriptAudio = (report) => {
    if (!agentSettings.hideTranscriptAudio || !agentSettings.hideBeforeDate) {
      return false;
    }
    const reportDate = new Date(report.callTime);
    const hideBeforeDate = new Date(agentSettings.hideBeforeDate);
    // Hide if call date is before the hideBeforeDate
    return reportDate < hideBeforeDate;
  };

  // Filter reports based on date range and status
  const filteredReports = reports.filter(report => {
    const reportDate = new Date(report.callTime);
    const matchesStatus = filterStatus === 'all' 
      ? true 
      : filterStatus === 'pending'
        ? !report.action_done || report.action_done === 'pending'
        : report.action_done === filterStatus;

    const matchesDateRange = (!dateRange.startDate || reportDate >= dateRange.startDate) &&
                            (!dateRange.endDate || reportDate <= new Date(dateRange.endDate.setHours(23, 59, 59, 999)));

    return matchesStatus && matchesDateRange;
  });

  if (loading) {
    return (
      <Container sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Loading reports...
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate('/agents')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" component="h1">
            Call Reports
          </Typography>
        </Box>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ p: 0 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        gap: 2
      }}>
        <Typography variant="h5">Call Reports</Typography>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Total Duration:
            </Typography>
            <Typography variant="subtitle1">
              {formatTotalDuration(calculateTotalSeconds())}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Timeline />}
            onClick={() => navigate(`/agents/${customAgentId}/call-duration`)}
            sx={{ minWidth: 'auto' }}
          >
            Call Duration
          </Button>
         <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DatePicker
                label="Start Date"
                value={dateRange.startDate}
                onChange={(newValue) => setDateRange(prev => ({ ...prev, startDate: newValue }))}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 200 }
                  }
                }}
              />
              <DatePicker
                label="End Date"
                value={dateRange.endDate}
                onChange={(newValue) => setDateRange(prev => ({ ...prev, endDate: newValue }))}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 200 }
                  }
                }}
              />
            </Box>
          </LocalizationProvider>
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <TableContainer 
          component={Paper} 
          sx={{ 
            width: '100%',
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '200px'
            }
          }}
        >
          <Table sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '50px' }} />
                <TableCell sx={{ fontWeight: 'bold', minWidth: '150px' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: '120px' }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: '120px' }}>Caller ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: '200px' }}>Email</TableCell>
                {user && user.role === 'admin' ? (
                  <>
                    <TableCell sx={{ fontWeight: 'bold', width: '100px' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span>Agent Duration</span>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '0.7rem' }}>
                          Total: {formatTotalDuration(calculateTotalAgentDuration())}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '120px' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span>Transfer Duration</span>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '0.7rem' }}>
                          Total: {formatTotalDuration(calculateTotalTransferDuration())}
                        </Typography>
                      </Box>
                    </TableCell>
                  </>
                ) : (
                  <TableCell sx={{ fontWeight: 'bold', width: '120px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <span>Call Duration</span>                      
                    </Box>
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 'bold', minWidth: '150px' }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '200px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <span>Actioned</span>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={user && user.role === 'admin' ? 10 : 9} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No {filterStatus === 'all' ? '' : filterStatus} call reports found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredReports.map((report) => (
                  <React.Fragment key={report._id}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleRowExpand(report._id)}
                        >
                          {expandedRows[report._id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell title={report.name}>{report.name}</TableCell>
                      <TableCell title={report.phone}>{report.phone}</TableCell>
                      <TableCell title={report.callerId}>{report.callerId || 'N/A'}</TableCell>
                      <TableCell title={report.email}>{report.email}</TableCell>
                      {user && user.role === 'admin' ? (
                        <>
                          <TableCell>{report.callDuration ? `${Math.floor(report.callDuration / 60)}:${(report.callDuration % 60).toString().padStart(2, '0')}` : 'N/A'}</TableCell>
                          <TableCell>
                            {(() => {
                              const transferDuration = calculateTransferDuration(report);
                              return transferDuration > 0 ? formatDuration(transferDuration) : 'N/A';
                            })()}
                          </TableCell>
                        </>
                      ) : (
                        <TableCell>
                          {(() => {
                            const callDuration = calculateCallDuration(report);
                            return callDuration > 0 ? formatDuration(callDuration) : 'N/A';
                          })()}
                        </TableCell>
                      )}
                      <TableCell>{new Date(report.callTime).toLocaleString()}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {report.objectUrl && !shouldHideTranscriptAudio(report) && (
                              <Tooltip title="Play Recording">
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(report.objectUrl, '_blank')}
                                >
                                  <PlayArrow />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!shouldHideTranscriptAudio(report) && (
                              <Tooltip title="Copy Transcript">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyTranscript(report.transcript)}
                                >
                                  <ContentCopy />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          {updatingStatus[report._id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Checkbox
                              checked={report.action_done === 'completed'}
                              onChange={(e) => handleStatusChange(report._id, e.target.checked)}
                              color="primary"
                              sx={{ 
                                p: 0,
                                '&.Mui-checked': {
                                  color: 'primary.main',
                                },
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={user && user.role === 'admin' ? 10 : 9}>
                        <Collapse in={expandedRows[report._id]} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 1 }}>
                            {report?.callBackNeeded && report.callBackNeeded !== 'NA' && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'bold' }}>
                                    Call Back Needed:&nbsp;
                                  </Typography>
                                  <Typography variant="body1" component="span">
                                    {report.callBackNeeded}
                                  </Typography>
                                </Box>
                            )}
                            {report?.client_type &&  report.client_type !== 'NA' && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'bold' }}>
                                  Client Type:&nbsp;
                                </Typography>
                                <Typography variant="body1" component="span">
                                  {report.client_type}
                                </Typography>
                              </Box>
                            )}
                            {report?.company &&   report.company !== 'NA' && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'bold' }}>
                                  Company:&nbsp;
                                </Typography>
                                <Typography variant="body1" component="span">
                                  {report.company}
                                </Typography>
                              </Box>
                            )}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle1" component="span" sx={{ fontWeight: 'bold' }}>
                                Call Purpose:&nbsp;
                              </Typography>
                              <Typography variant="body1" component="span">
                                {report.callPurpose || 'N/A'}
                              </Typography>
                            </Box>
                            {!shouldHideTranscriptAudio(report) ? (
                              <>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                  Transcript
                                </Typography>
                                <Box 
                                  sx={{ 
                                    p: 2, 
                                    bgcolor: 'background.paper', 
                                    borderRadius: 1,
                                    whiteSpace: 'pre-wrap',
                                    maxHeight: '300px',
                                    overflowY: 'auto'
                                  }}
                                  dangerouslySetInnerHTML={{ 
                                    __html: report.transcript.replace(/<strong>/g, '<b>').replace(/<\/strong>/g, '</b>')
                                  }}
                                />
                              </>
                            ) : (
                              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Transcript and audio are removed for calls before {new Date(agentSettings.hideBeforeDate).toLocaleDateString()}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mt: 3, 
        mb: 3 
      }}>
        <Pagination 
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>
    </Container>
  );
};

export default CallReportList; 